import { type LoginAttemptListApiData, type LoginAttemptSystemBlock } from '@navio/nsdk';
import { type FilterControl, filterUnitFor, filterUrlState, type UrlValues, useFilterUnits } from 'noddi-ui-web';

import {
  deviceKindOptions,
  FILTERABLE_BLOCKS,
  HELD_BY_US,
  inBlockGroup,
  methodOptions,
  resultOptions,
  SPAM_BLOCKS,
  stageOptions,
  systemBlockOptions
} from './loginAttemptColumns';

/** The list narrowed to one customer or one account. No control writes these; the page fixes them. */
export type LoginAttemptScope = {
  userGroupIds?: number[];
  userIds?: number[];
};

// The endpoint marks some params required; each unit still writes its own, so bind to the optional form.
const loginAttemptFilterUnit =
  filterUnitFor<
    Partial<Omit<NonNullable<LoginAttemptListApiData['query']>, 'pageIndex' | 'pageSize' | 'ordering' | 'search'>>
  >();

const SYSTEM_BLOCKS_URL_KEY = filterUrlState.urlKey('systemBlocks');
const EXCLUDED_SYSTEM_BLOCKS_URL_KEY = filterUrlState.urlKey('excludedSystemBlocks');
const parseSystemBlock = (value: string) => FILTERABLE_BLOCKS.find((block) => block === value);

type BlockGroup = { id: string; title: string; description: string; members: readonly LoginAttemptSystemBlock[] };

const BLOCK_GROUPS: BlockGroup[] = [
  {
    id: 'systemBlocksHeld',
    title: 'Blocked by us',
    description: 'A block list, a rate limit, or a disallowed country',
    members: HELD_BY_US
  },
  {
    id: 'systemBlocksSpam',
    title: 'Spam',
    description: 'CAPTCHA, SMS pumping, or a Twilio fraud block',
    members: SPAM_BLOCKS
  }
];

/**
 * The blocks the backend keeps in one `systemBlocks` param, split into two controls -- the ones we hold back and
 * the spam checks -- because staff look for one or the other, never the raw list. Each control writes only its
 * own members of the param, include and exclude together.
 */
const systemBlockFilter = loginAttemptFilterUnit.compose(
  'systemBlocks',
  {
    [SYSTEM_BLOCKS_URL_KEY]: filterUrlState.selection(parseSystemBlock),
    [EXCLUDED_SYSTEM_BLOCKS_URL_KEY]: filterUrlState.excluded(parseSystemBlock)
  },
  (values: UrlValues, setValues) => {
    const exclude = (values[EXCLUDED_SYSTEM_BLOCKS_URL_KEY] as LoginAttemptSystemBlock[] | undefined) ?? [];
    const include = filterUrlState.includedValues(
      { include: values[SYSTEM_BLOCKS_URL_KEY] as LoginAttemptSystemBlock[] | null | undefined, exclude },
      []
    );
    const controls = BLOCK_GROUPS.map(({ id, title, description, members }): FilterControl => {
      const inGroup = (value: LoginAttemptSystemBlock) => inBlockGroup(members, value);
      return {
        id,
        type: 'multi-select',
        title,
        description,
        options: systemBlockOptions(members),
        canExclude: true,
        value: { include: include.filter(inGroup), exclude: exclude.filter(inGroup) },
        onChange: (next) =>
          setValues((latest) => {
            const latestExclude =
              (latest[EXCLUDED_SYSTEM_BLOCKS_URL_KEY] as LoginAttemptSystemBlock[] | undefined) ?? [];
            const latestInclude = (latest[SYSTEM_BLOCKS_URL_KEY] as LoginAttemptSystemBlock[] | null | undefined) ?? [];
            const pickedExclude = next.exclude.flatMap((value) => parseSystemBlock(String(value)) ?? []);
            const pickedInclude = next.include
              .flatMap((value) => parseSystemBlock(String(value)) ?? [])
              .filter((value) => !pickedExclude.includes(value));
            // The other group's blocks stay as they are.
            const nextExclude = [...latestExclude.filter((value) => !inGroup(value)), ...pickedExclude];
            const nextInclude = [
              ...latestInclude.filter((value) => !inGroup(value) && !nextExclude.includes(value)),
              ...pickedInclude
            ];
            return {
              [SYSTEM_BLOCKS_URL_KEY]: nextInclude.length > 0 ? nextInclude : undefined,
              [EXCLUDED_SYSTEM_BLOCKS_URL_KEY]: nextExclude
            };
          }),
        reset: () =>
          setValues((latest) => {
            const latestExclude =
              (latest[EXCLUDED_SYSTEM_BLOCKS_URL_KEY] as LoginAttemptSystemBlock[] | undefined) ?? [];
            const latestInclude = (latest[SYSTEM_BLOCKS_URL_KEY] as LoginAttemptSystemBlock[] | null | undefined) ?? [];
            const keptInclude = latestInclude.filter((value) => !inGroup(value));
            return {
              [SYSTEM_BLOCKS_URL_KEY]: keptInclude.length > 0 ? keptInclude : undefined,
              [EXCLUDED_SYSTEM_BLOCKS_URL_KEY]: latestExclude.filter((value) => !inGroup(value))
            };
          }),
        isInitial: !include.some(inGroup) && !exclude.some(inGroup)
      };
    });
    return {
      controls,
      params: {
        systemBlocks: include.length > 0 ? include : undefined,
        excludedSystemBlocks: exclude.length > 0 ? exclude : undefined
      }
    };
  }
);

/** Every filter the login attempt list supports, held in the URL so a view can be shared as a link. */
export const useLoginAttemptFilters = (scope: LoginAttemptScope = {}) =>
  useFilterUnits([
    loginAttemptFilterUnit.locked(scope),
    loginAttemptFilterUnit.dateRange('createdAt', {
      title: 'When',
      description: 'The calendar day the attempt happened',
      exclude: true
    }),
    loginAttemptFilterUnit.timeRange('createdAtTime', {
      title: 'Time of day',
      description: 'The clock time the attempt happened, for a burst outside opening hours'
    }),
    loginAttemptFilterUnit.multiSelect('methods', { title: 'Method', options: methodOptions, exclude: true }),
    loginAttemptFilterUnit.multiSelect('stages', { title: 'Step', options: stageOptions, exclude: true }),
    loginAttemptFilterUnit.multiSelect('results', { title: 'Result', options: resultOptions, exclude: true }),
    loginAttemptFilterUnit.multiSelect('deviceKinds', {
      title: 'Device',
      description: 'Desktop, mobile, tablet, or a bot, read from the user agent',
      options: deviceKindOptions,
      exclude: true
    }),
    systemBlockFilter
  ]);
